"use client";

export default function LogisticsDashboardsSection() {
  return (
    <section className="py-24 bg-cream px-4 sm:px-6 lg:px-8 xl:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-navy text-2xl sm:text-3xl md:text-4xl xl:text-[3rem] leading-[1.2] font-bold mb-4">
            Role-Specific Intelligence for Every <span className="italic">Stakeholder</span>
          </h2>
          <p className="text-charcoal/70 text-lg max-w-2xl mx-auto">
            Purpose-built dashboards for every decision-maker in your logistics organisation.
          </p>
        </div>

        {/* Dashboard Cards */}
        <div className="space-y-12">

          {/* Dashboard 1: CEO / CFO */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Info */}
              <div className="p-8 md:p-10 flex flex-col justify-between">
                <div>
                  <div className="inline-block bg-navy/8 text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Dashboard 1</div>
                  <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold mb-4">CEO / CFO Dashboard</h3>
                  <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">Executive-level visibility into revenue performance, fleet utilisation, OTIF trends, and lane profitability.</p>
                  <div className="space-y-2 mb-8">
                    {['Revenue & Profitability by Lane/Customer', 'Fleet Utilization & Empty Miles', 'OTIF Performance & Customer Satisfaction', 'Cost per Shipment & Margin']?.map((kpi, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0"></div>
                        <span className="text-charcoal text-sm">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-300">View Demo</button>
                  <button className="bg-white hover:bg-gold/10 text-navy font-semibold px-5 py-2.5 rounded-lg border-2 border-navy text-sm transition-all duration-300">Request Customization</button>
                </div>
              </div>
              {/* Right: SVG Dashboard Mockup */}
              <div className="bg-[#0f1a2e] p-6 flex items-center justify-center min-h-[340px]">
                <svg viewBox="0 0 420 280" className="w-full max-w-[420px]" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="420" height="280" rx="8" fill="#1B2845" />
                  {/* Top bar */}
                  <rect x="0" y="0" width="420" height="32" rx="8" fill="#0f1a2e" />
                  <rect x="0" y="24" width="420" height="8" fill="#0f1a2e" />
                  <circle cx="14" cy="16" r="5" fill="#C5A572" opacity="0.6" />
                  <circle cx="28" cy="16" r="5" fill="#C5A572" opacity="0.3" />
                  <circle cx="42" cy="16" r="5" fill="#C5A572" opacity="0.15" />
                  <text x="210" y="20" textAnchor="middle" fill="#C5A572" fontSize="9" fontFamily="serif" opacity="0.8">CEO / CFO Executive Dashboard</text>

                  {/* KPI Cards Row */}
                  {[
                    { x: 8,   label: '₹312Cr', sub: 'Revenue',     color: '#C5A572' },
                    { x: 112, label: '82%',    sub: 'Fleet Util.',  color: '#4ade80' },
                    { x: 216, label: '93%',    sub: 'OTIF Rate',    color: '#60a5fa' },
                    { x: 320, label: '12%',    sub: 'Empty Miles',  color: '#f472b6' },
                  ]?.map((kpi, i) => (
                    <g key={i}>
                      <rect x={kpi?.x} y="40" width="96" height="44" rx="4" fill="#ffffff08" stroke="#ffffff10" strokeWidth="0.5" />
                      <text x={kpi?.x + 48} y="58" textAnchor="middle" fill={kpi?.color} fontSize="13" fontWeight="bold" fontFamily="sans-serif">{kpi?.label}</text>
                      <text x={kpi?.x + 48} y="72" textAnchor="middle" fill="#ffffff60" fontSize="7" fontFamily="sans-serif">{kpi?.sub}</text>
                    </g>
                  ))}

                  {/* OTIF Trend Line Chart */}
                  <rect x="8" y="92" width="200" height="100" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="104" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">OTIF Rate Trend (%)</text>
                  <line x1="20" y1="182" x2="200" y2="182" stroke="#ffffff20" strokeWidth="0.5" />
                  <line x1="20" y1="112" x2="20" y2="182" stroke="#ffffff20" strokeWidth="0.5" />
                  <path d="M 20 168 L 48 162 L 76 158 L 104 150 L 132 145 L 160 138 L 188 132" fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M 20 168 L 48 162 L 76 158 L 104 150 L 132 145 L 160 138 L 188 132 L 188 182 L 20 182 Z" fill="#60a5fa" opacity="0.08" />
                  {[[20,168],[48,162],[76,158],[104,150],[132,145],[160,138],[188,132]]?.map(([x,y],i) => (
                    <circle key={i} cx={x} cy={y} r="2" fill="#60a5fa" />
                  ))}
                  {['Jul','Aug','Sep','Oct','Nov','Dec','Jan']?.map((m, i) => (
                    <text key={i} x={20 + i * 28} y="190" fill="#ffffff40" fontSize="6" textAnchor="middle" fontFamily="sans-serif">{m}</text>
                  ))}

                  {/* Revenue by Lane Bar Chart */}
                  <rect x="216" y="92" width="196" height="100" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="224" y="104" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Revenue by Lane (₹Cr)</text>
                  {[
                    { label: 'Mum–Del', val: 140, color: '#C5A572' },
                    { label: 'Del–Blr', val: 115, color: '#C5A572' },
                    { label: 'Hyd–Che', val: 95,  color: '#C5A572' },
                    { label: 'Pune–Kol', val: 78, color: '#C5A572' },
                    { label: 'Blr–Hyd', val: 62,  color: '#C5A572' },
                  ]?.map((bar, i) => (
                    <g key={i}>
                      <text x="224" y={118 + i * 14} fill="#ffffff60" fontSize="6" fontFamily="sans-serif">{bar?.label}</text>
                      <rect x="272" y={110 + i * 14} width={bar?.val * 0.85} height="8" rx="2" fill={bar?.color} opacity="0.55" />
                      <text x={272 + bar?.val * 0.85 + 3} y={118 + i * 14} fill="#C5A572" fontSize="6" fontFamily="sans-serif">{bar?.val}</text>
                    </g>
                  ))}

                  {/* Fleet Utilisation Gauge */}
                  <rect x="8" y="200" width="130" height="72" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="212" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Fleet Utilisation</text>
                  <path d="M 30 258 A 43 43 0 0 1 116 258" fill="none" stroke="#ffffff15" strokeWidth="8" strokeLinecap="round" />
                  <path d="M 30 258 A 43 43 0 0 1 103 228" fill="none" stroke="#4ade80" strokeWidth="8" strokeLinecap="round" />
                  <text x="73" y="252" textAnchor="middle" fill="#4ade80" fontSize="14" fontWeight="bold" fontFamily="sans-serif">82%</text>
                  <text x="73" y="264" textAnchor="middle" fill="#ffffff40" fontSize="6" fontFamily="sans-serif">Utilised</text>

                  {/* Cost per Ton-KM KPI */}
                  <rect x="146" y="200" width="130" height="72" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="154" y="212" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Cost per Ton-KM</text>
                  <text x="211" y="248" textAnchor="middle" fill="#C5A572" fontSize="20" fontWeight="bold" fontFamily="sans-serif">₹7.20</text>
                  <text x="211" y="262" textAnchor="middle" fill="#ffffff40" fontSize="6" fontFamily="sans-serif">↓15% vs last quarter</text>

                  {/* Empty Miles KPI */}
                  <rect x="284" y="200" width="128" height="72" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="292" y="212" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Empty Miles %</text>
                  <text x="348" y="248" textAnchor="middle" fill="#f472b6" fontSize="20" fontWeight="bold" fontFamily="sans-serif">12%</text>
                  <text x="348" y="262" textAnchor="middle" fill="#ffffff40" fontSize="6" fontFamily="sans-serif">↓50% improvement</text>
                </svg>
              </div>
            </div>
          </div>

          {/* Dashboard 2: Operations / Dispatch */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Info */}
              <div className="p-8 md:p-10 flex flex-col justify-between order-2 lg:order-1">
                <div>
                  <div className="inline-block bg-navy/8 text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Dashboard 2</div>
                  <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold mb-4">Operations / Dispatch Dashboard</h3>
                  <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">Live fleet visibility, shipment status tracking, driver performance, and route efficiency in real time.</p>
                  <div className="space-y-2 mb-8">
                    {['Shipment Status & ETA Accuracy', 'Driver Availability & Performance', 'Route Efficiency & Fuel Costs', 'Exception Management']?.map((kpi, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0"></div>
                        <span className="text-charcoal text-sm">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-300">View Demo</button>
                  <button className="bg-white hover:bg-gold/10 text-navy font-semibold px-5 py-2.5 rounded-lg border-2 border-navy text-sm transition-all duration-300">Request Customization</button>
                </div>
              </div>
              {/* Right: SVG Dashboard Mockup */}
              <div className="bg-[#0f1a2e] p-6 flex items-center justify-center min-h-[340px] order-1 lg:order-2">
                <svg viewBox="0 0 420 280" className="w-full max-w-[420px]" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="420" height="280" rx="8" fill="#1B2845" />
                  <rect x="0" y="0" width="420" height="32" rx="8" fill="#0f1a2e" />
                  <rect x="0" y="24" width="420" height="8" fill="#0f1a2e" />
                  <circle cx="14" cy="16" r="5" fill="#C5A572" opacity="0.6" />
                  <circle cx="28" cy="16" r="5" fill="#C5A572" opacity="0.3" />
                  <circle cx="42" cy="16" r="5" fill="#C5A572" opacity="0.15" />
                  <text x="210" y="20" textAnchor="middle" fill="#C5A572" fontSize="9" fontFamily="serif" opacity="0.8">Operations / Dispatch Dashboard</text>

                  {/* Live Fleet Map Grid */}
                  <rect x="8" y="40" width="200" height="130" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Live Fleet Map — GPS Tracking</text>
                  {/* Map grid */}
                  {[60,80,100,120,140,160]?.map((y,i) => (
                    <line key={`mg-h-${i}`} x1="12" y1={y} x2="204" y2={y} stroke="#ffffff08" strokeWidth="0.5" />
                  ))}
                  {[30,60,90,120,150,180,200]?.map((x,i) => (
                    <line key={`mg-v-${i}`} x1={x} y1="56" x2={x} y2="164" stroke="#ffffff08" strokeWidth="0.5" />
                  ))}
                  {/* Route lines */}
                  <path d="M 30 80 L 80 100 L 140 75 L 190 110" stroke="#C5A572" strokeWidth="1" fill="none" strokeOpacity="0.4" strokeDasharray="4 2" />
                  <path d="M 50 130 L 100 115 L 160 140 L 195 125" stroke="#60a5fa" strokeWidth="1" fill="none" strokeOpacity="0.4" strokeDasharray="4 2" />
                  {/* GPS dots — on-time green */}
                  {[[80,100],[140,75],[100,115],[160,140]]?.map(([x,y],i) => (
                    <g key={`gps-${i}`}>
                      <circle cx={x} cy={y} r="4" fill="#4ade80" opacity="0.8" />
                      <circle cx={x} cy={y} r="8" fill="none" stroke="#4ade80" strokeWidth="0.8" opacity="0.3" />
                    </g>
                  ))}
                  {/* Delayed red */}
                  {[[190,110],[195,125]]?.map(([x,y],i) => (
                    <g key={`gps-d-${i}`}>
                      <circle cx={x} cy={y} r="4" fill="#f87171" opacity="0.8" />
                      <circle cx={x} cy={y} r="8" fill="none" stroke="#f87171" strokeWidth="0.8" opacity="0.3" />
                    </g>
                  ))}
                  {/* Legend */}
                  <circle cx="16" cy="158" r="3" fill="#4ade80" opacity="0.8" />
                  <text x="22" y="161" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">On-time</text>
                  <circle cx="60" cy="158" r="3" fill="#f87171" opacity="0.8" />
                  <text x="66" y="161" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">Delayed</text>
                  <circle cx="104" cy="158" r="3" fill="#fbbf24" opacity="0.8" />
                  <text x="110" y="161" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">At-risk</text>

                  {/* Shipments In-Transit Status */}
                  <rect x="216" y="40" width="196" height="130" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="224" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Shipments In-Transit</text>
                  {[
                    { id: 'SHP-1042', status: 'On-Time',  eta: '14:30', color: '#4ade80' },
                    { id: 'SHP-1038', status: 'Delayed',  eta: '17:15', color: '#f87171' },
                    { id: 'SHP-1055', status: 'On-Time',  eta: '15:00', color: '#4ade80' },
                    { id: 'SHP-1061', status: 'At-Risk',  eta: '16:45', color: '#fbbf24' },
                    { id: 'SHP-1029', status: 'On-Time',  eta: '13:50', color: '#4ade80' },
                    { id: 'SHP-1073', status: 'Delayed',  eta: '18:00', color: '#f87171' },
                  ]?.map((s, i) => (
                    <g key={i}>
                      <rect x="220" y={58 + i * 17} width="188" height="14" rx="2" fill="#ffffff04" />
                      <text x="226" y={69 + i * 17} fill="#ffffff70" fontSize="6.5" fontFamily="sans-serif">{s?.id}</text>
                      <rect x="290" y={60 + i * 17} width="40" height="10" rx="2" fill={s?.color} opacity="0.2" />
                      <text x="310" y={69 + i * 17} textAnchor="middle" fill={s?.color} fontSize="6" fontFamily="sans-serif">{s?.status}</text>
                      <text x="390" y={69 + i * 17} textAnchor="end" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">ETA {s?.eta}</text>
                    </g>
                  ))}

                  {/* Driver Performance Leaderboard */}
                  <rect x="8" y="178" width="196" height="94" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="190" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Driver Performance Leaderboard</text>
                  {[
                    { name: 'Rajan K.',  score: 96, bar: 148 },
                    { name: 'Suresh M.', score: 91, bar: 140 },
                    { name: 'Priya V.',  score: 88, bar: 135 },
                    { name: 'Amit D.',   score: 84, bar: 129 },
                    { name: 'Neha S.',   score: 79, bar: 121 },
                  ]?.map((d, i) => (
                    <g key={i}>
                      <text x="16" y={204 + i * 13} fill="#ffffff60" fontSize="6.5" fontFamily="sans-serif">{d?.name}</text>
                      <rect x="72" y={196 + i * 13} width={d?.bar * 0.7} height="7" rx="2" fill="#C5A572" opacity="0.45" />
                      <text x="178" y={204 + i * 13} fill="#C5A572" fontSize="6.5" fontFamily="sans-serif">{d?.score}</text>
                    </g>
                  ))}

                  {/* Fuel Consumption Trend */}
                  <rect x="212" y="178" width="200" height="94" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="220" y="190" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Fuel Consumption Trend (L/100km)</text>
                  <line x1="220" y1="262" x2="408" y2="262" stroke="#ffffff20" strokeWidth="0.5" />
                  <polyline points="224,252 252,248 280,244 308,240 336,236 364,232 392,228" fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M 224 252 L 252 248 L 280 244 L 308 240 L 336 236 L 364 232 L 392 228 L 392 262 L 224 262 Z" fill="#fbbf24" opacity="0.07" />
                  {['Jul','Aug','Sep','Oct','Nov','Dec','Jan']?.map((m, i) => (
                    <text key={i} x={224 + i * 28} y="270" fill="#ffffff40" fontSize="5.5" textAnchor="middle" fontFamily="sans-serif">{m}</text>
                  ))}
                </svg>
              </div>
            </div>
          </div>

          {/* Dashboard 3: Warehouse / Fulfillment */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Info */}
              <div className="p-8 md:p-10 flex flex-col justify-between">
                <div>
                  <div className="inline-block bg-navy/8 text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Dashboard 3</div>
                  <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold mb-4">Warehouse / Fulfillment Dashboard</h3>
                  <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">Complete warehouse intelligence: capacity, SKU velocity, picking accuracy, and dock utilisation heatmap.</p>
                  <div className="space-y-2 mb-8">
                    {['Warehouse Space & Labor Utilization', 'SKU Turnover & Inventory Health', 'Picking & Packing Efficiency', 'Dock Productivity']?.map((kpi, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0"></div>
                        <span className="text-charcoal text-sm">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-300">View Demo</button>
                  <button className="bg-white hover:bg-gold/10 text-navy font-semibold px-5 py-2.5 rounded-lg border-2 border-navy text-sm transition-all duration-300">Request Customization</button>
                </div>
              </div>
              {/* Right: SVG Dashboard Mockup */}
              <div className="bg-[#0f1a2e] p-6 flex items-center justify-center min-h-[340px]">
                <svg viewBox="0 0 420 280" className="w-full max-w-[420px]" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="420" height="280" rx="8" fill="#1B2845" />
                  <rect x="0" y="0" width="420" height="32" rx="8" fill="#0f1a2e" />
                  <rect x="0" y="24" width="420" height="8" fill="#0f1a2e" />
                  <circle cx="14" cy="16" r="5" fill="#C5A572" opacity="0.6" />
                  <circle cx="28" cy="16" r="5" fill="#C5A572" opacity="0.3" />
                  <circle cx="42" cy="16" r="5" fill="#C5A572" opacity="0.15" />
                  <text x="210" y="20" textAnchor="middle" fill="#C5A572" fontSize="9" fontFamily="serif" opacity="0.8">Warehouse / Fulfillment Dashboard</text>

                  {/* Warehouse Capacity Gauge */}
                  <rect x="8" y="40" width="120" height="110" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Capacity Utilisation</text>
                  <path d="M 28 128 A 40 40 0 0 1 108 128" fill="none" stroke="#ffffff15" strokeWidth="8" strokeLinecap="round" />
                  <path d="M 28 128 A 40 40 0 0 1 100 100" fill="none" stroke="#C5A572" strokeWidth="8" strokeLinecap="round" />
                  <text x="68" y="122" textAnchor="middle" fill="#C5A572" fontSize="14" fontWeight="bold" fontFamily="sans-serif">85%</text>
                  <text x="68" y="134" textAnchor="middle" fill="#ffffff40" fontSize="6" fontFamily="sans-serif">Utilised</text>

                  {/* KPI Cards */}
                  <rect x="136" y="40" width="130" height="50" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="144" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Picking Accuracy</text>
                  <text x="201" y="78" textAnchor="middle" fill="#4ade80" fontSize="18" fontWeight="bold" fontFamily="sans-serif">98.5%</text>

                  <rect x="274" y="40" width="138" height="50" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="282" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Labor Productivity</text>
                  <text x="343" y="78" textAnchor="middle" fill="#60a5fa" fontSize="18" fontWeight="bold" fontFamily="sans-serif">42 ord/h</text>

                  {/* Inbound/Outbound Dual-Axis Chart */}
                  <rect x="136" y="98" width="276" height="82" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="144" y="110" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Inbound / Outbound Volumes</text>
                  <line x1="148" y1="170" x2="404" y2="170" stroke="#ffffff20" strokeWidth="0.5" />
                  {/* Inbound bars */}
                  {[
                    { x: 156, h: 38 }, { x: 192, h: 44 }, { x: 228, h: 36 },
                    { x: 264, h: 50 }, { x: 300, h: 42 }, { x: 336, h: 48 }, { x: 372, h: 54 }
                  ]?.map((b, i) => (
                    <g key={i}>
                      <rect x={b?.x} y={170 - b?.h} width="14" height={b?.h} rx="1" fill="#C5A572" opacity="0.55" />
                      <rect x={b?.x + 16} y={170 - b?.h * 0.85} width="14" height={b?.h * 0.85} rx="1" fill="#60a5fa" opacity="0.45" />
                    </g>
                  ))}
                  {['Jul','Aug','Sep','Oct','Nov','Dec','Jan']?.map((m, i) => (
                    <text key={i} x={163 + i * 36} y="178" fill="#ffffff40" fontSize="5.5" textAnchor="middle" fontFamily="sans-serif">{m}</text>
                  ))}
                  {/* Legend */}
                  <rect x="148" y="112" width="8" height="5" rx="1" fill="#C5A572" opacity="0.55" />
                  <text x="159" y="117" fill="#ffffff50" fontSize="5.5" fontFamily="sans-serif">Inbound</text>
                  <rect x="196" y="112" width="8" height="5" rx="1" fill="#60a5fa" opacity="0.45" />
                  <text x="207" y="117" fill="#ffffff50" fontSize="5.5" fontFamily="sans-serif">Outbound</text>

                  {/* Dock Utilisation Heatmap */}
                  <rect x="8" y="158" width="120" height="114" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="170" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Dock Utilisation</text>
                  {/* Heatmap grid */}
                  {['D1','D2','D3','D4']?.map((dock, di) =>
                    ['6am','9am','12pm','3pm','6pm']?.map((slot, si) => {
                      const vals = [[0.9,0.7,0.5,0.8,0.6],[0.4,0.9,0.8,0.6,0.3],[0.6,0.5,0.9,0.7,0.8],[0.3,0.6,0.7,0.9,0.5]];
                      const v = vals?.[di]?.[si];
                      const r = Math.round(v * 197 + 27);
                      const g = Math.round((1 - v) * 100 + 40);
                      const b = 40;
                      return (
                        <g key={`hm-${di}-${si}`}>
                          <rect x={16 + si * 20} y={178 + di * 22} width="18" height="18" rx="2" fill={`rgb(${r},${g},${b})`} opacity="0.7" />
                          {di === 0 && <text x={25 + si * 20} y="176" fill="#ffffff30" fontSize="5" textAnchor="middle" fontFamily="sans-serif">{slot}</text>}
                          {si === 0 && <text x="14" y={190 + di * 22} fill="#ffffff40" fontSize="5.5" textAnchor="end" fontFamily="sans-serif">{dock}</text>}
                        </g>
                      );
                    })
                  )}

                  {/* SKU Velocity Bubble Chart */}
                  <rect x="136" y="188" width="276" height="84" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="144" y="200" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">SKU Velocity (Fast / Slow Movers)</text>
                  {[
                    { cx: 175, cy: 240, r: 18, label: 'SKU-A', color: '#4ade80' },
                    { cx: 220, cy: 228, r: 14, label: 'SKU-B', color: '#4ade80' },
                    { cx: 265, cy: 248, r: 10, label: 'SKU-C', color: '#fbbf24' },
                    { cx: 305, cy: 232, r: 8,  label: 'SKU-D', color: '#f87171' },
                    { cx: 345, cy: 244, r: 6,  label: 'SKU-E', color: '#f87171' },
                    { cx: 385, cy: 236, r: 5,  label: 'SKU-F', color: '#f87171' },
                  ]?.map((b, i) => (
                    <g key={i}>
                      <circle cx={b?.cx} cy={b?.cy} r={b?.r} fill={b?.color} opacity="0.3" />
                      <circle cx={b?.cx} cy={b?.cy} r={b?.r} fill="none" stroke={b?.color} strokeWidth="1" opacity="0.6" />
                      <text x={b?.cx} y={b?.cy + 3} textAnchor="middle" fill="#ffffff70" fontSize="5" fontFamily="sans-serif">{b?.label}</text>
                    </g>
                  ))}
                  {/* Legend */}
                  <circle cx="148" cy="264" r="4" fill="#4ade80" opacity="0.5" />
                  <text x="155" y="267" fill="#ffffff40" fontSize="5.5" fontFamily="sans-serif">Fast</text>
                  <circle cx="178" cy="264" r="4" fill="#fbbf24" opacity="0.5" />
                  <text x="185" y="267" fill="#ffffff40" fontSize="5.5" fontFamily="sans-serif">Medium</text>
                  <circle cx="218" cy="264" r="4" fill="#f87171" opacity="0.5" />
                  <text x="225" y="267" fill="#ffffff40" fontSize="5.5" fontFamily="sans-serif">Slow</text>
                </svg>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}